<template>
    <div v-if="ready" class="my-select" :class="{'is-focus': isOpen, 'has-value': hasValue}">
        <multiselect
            v-model="current_value"
            :options="options"
            ref="select"
            :name="name"
            :track-by="trackBy"
            :class="additionalClass"
            :label="label"
            :searchable="searchable"
            :multiple="multiple"
            :disabled="disabled"
            :show-labels="showLabels"
            :allow-empty="allowEmpty"
            @open="onFocus"
            @close="onBlur"
            @input="onInput"
            :custom-label="customLabel"
            :group-values="groupValues"
            :group-label="groupLabel"
            :group-select="false"
            :clear-on-select="clearOnSelect"
            :close-on-select="closeOnSelect"
        >
            <template v-if="props.option.name" slot="option" slot-scope="props">
                <span :class="props.option.class">{{ props.option.name }}</span>
            </template>

            <template slot="noOptions" slot-scope="props">
                {{ lang.no_value|capitalize }}
            </template>

            <template v-if="multiple" slot="selection" slot-scope="{values, search, isOpen}">
                <span class="multiselect__single" v-if="current_value && current_value.length > 1">{{ current_value.length }} {{ customMultiselectLabel }}</span>
                <span class="multiselect__single" v-else-if="current_value && current_value.length">{{ current_value[0].name }}</span>
            </template>
        </multiselect>

        <div
            class="my-select-label"
            :class="{ 'network-color': isOpen }"
            @click="$refs.select.activate()"
        >
            {{ placeholder | capitalize }}
        </div>

        <template v-if="clearable && hasValue">
            <button @click.stop.prevent="clearInput" class="clearable">
                <img src="/img/icons/clear-search.svg" alt="Reset">
            </button>
        </template>
    </div>
</template>

<script>
import Multiselect from 'vue-multiselect'

export default {
    components: {
        'multiselect': Multiselect,
    },

    props: {
        allowEmpty: {
            type: Boolean,
            default: true,
        },
        customLabel: {
            type: Function,
            required: false
        },
        customMultiselectLabel: {
            type: String,
            required: false
        },
        additionalClass: {
            type: String,
            required: false,
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        closeOnSelect: {
            type: Boolean,
            default: true,
        },
        clearOnSelect: {
            type: Boolean,
            default: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        groupLabel: {
            type: String,
            required: false,
        },
        groupValues: {
            type: String,
            required: false,
        },
        label: {
            type: String,
            required: false,
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        name: {
            type: String,
            required: false,
        },
        options: {
            type: Array | Object,
            required: true,
        },
        placeholder: {
            type: String,
            required: false,
        },
        searchable: {
            type: Boolean,
            default: false,
        },
        showLabels: {
            type: Boolean,
            default: false,
        },
        trackBy: {
            type: String,
            required: false,
        },
        type: {
            type: String,
            default: 'text',
        },
        value: {
            type: [String, Number, Object, Array],
            required: false,
        },
    },

    data() {
        return {
            current_placeholder: this.placeholder,
            isFocus: false,
            current_value: this.value,
        }
    },

    computed: {
        hasValue() {
            return this.current_value != ''
                && this.current_value != null
                && this.current_value != undefined
                && this.current_value
        },

        isOpen() {
            return this.isFocus // || (this.current_value != null && this.current_value != '')
        },

        lang() {
            return this.$root.lang.words ?? {}
        },

        ready() {
            return this.lang
        },
    },

    mounted() {
       this.setCurrentValue()

    },

    watch: {
        placeholder() {
            //this.current_value = ''

            this.current_placeholder = this.placeholder
        },

        value(newVal) {
            this.current_value = newVal
            this.setCurrentValue()
        },

    },

    methods: {
        setCurrentValue() {
            if (this.trackBy && typeof this.current_value !== 'object' && this.current_value) {
                if (!this.groupValues) {
                    this.current_value = this.options.find(option => {
                        return option[this.trackBy] == this.current_value
                    })
                } else {
                    let check_value = false
                    this.options.find(group => {
                        if (!check_value) {
                            let value = group.groupValues.find(option => {
                                if (option[this.trackBy] == this.current_value) {
                                    check_value = true
                                    return true
                                }
                            })

                            if (value) {
                                this.current_value = value
                            }
                        }
                    })
                }
            }
        },

        checkFocus() {
            if(! this.current_value) {
                this.isFocus = false
            }
        },

        onBlur() {
            this.isFocus = false

            this.$emit('blur')
        },

        onFocus() {
            this.isFocus = true
            this.current_placeholder = ''

            this.$emit('focus')
        },

        onInput() {
            this.checkFocus()

            let value = this.current_value

            if (typeof value === 'object' && value) {
                if (value[this.label] &&  value[this.trackBy]) {
                    this.$emit('input', value[this.trackBy])
                    this.$emit('inputLabel', value[this.label])
                } else {
                    this.$emit('input', value)
                }
            }
        },

        clearInput() {
            this.current_value = null

            this.$emit('input', this.current_value)
        },

        activate() {
            this.$refs.select.activate()
        }
    },
}
</script>

<style lang="sass" scoped>
    .my-select
        position: relative

        .my-select-label
            position: absolute
            top: .8rem
            left: 0
            margin-left: .75rem
            color: grey
            transition: all .2s

        button.clearable
            position: absolute
            top: 4px
            right: 8px
            background: none
            border: none
            font-size: 12px
            padding: 0 5px

            &:focus
                outline: none

        &.has-value
            select
                padding-top: 1.3rem

            .my-select-label
                top: 0.5rem
                font-size: 0.75rem
                height: auto
                line-height: 1

        .clearable
            img
                width: 1rem
                position: absolute
                top: 0.8rem
                right: 1.3rem

    .multiselect--active
        & + .my-select-label
            z-index: 50
</style>